import { concatTypedArrays, createUint24 } from "../util";

class BufferedReader {
    constructor(reader) {
        this.reader = reader;
        this.buffer = new Uint8Array(0); // Initial empty buffer
    }

    // Reads the specified number of bytes, buffering any extra bytes.
    // The read operation can timeout if no data is received within the specified time.
    async read(numBytes, timeout = null) {
        const bytes = [];
        const startTime = Date.now();

        while (bytes.length < numBytes) {
            if (this.buffer.length > 0) {
                // If there are bytes in the buffer, consume them
                const needed = numBytes - bytes.length;
                const toRead = Math.min(needed, this.buffer.length);
                bytes.push(...this.buffer.slice(0, toRead));
                this.buffer = this.buffer.slice(toRead);
            } else {
                // If the buffer is empty, read from the reader
                let readPromise = this.reader.read();

                // If a timeout is specified, create a race between the read and the timeout
                if (timeout !== null) {
                    readPromise = Promise.race([
                        readPromise,
                        new Promise((_, reject) =>
                            setTimeout(() => reject(new Error("Read timeout")), timeout)
                        ),
                    ]);
                }

                try {
                    const { value, done } = await readPromise;
                    if (done || !value || value.length === 0) {
                        break; // No more data available
                    }

                    const needed = numBytes - bytes.length;
                    if (value.length <= needed) {
                        bytes.push(...value);
                    } else {
                        bytes.push(...value.slice(0, needed));
                        this.buffer = value.slice(needed); // Buffer the extra bytes
                    }
                } catch (error) {
                    if (error.message === "Read timeout") {
                        console.warn("Read operation timed out");
                    } else {
                        console.error("Error while reading data:", error);
                    }
                    break;
                }
            }

            // Check if the total time has exceeded the timeout
            if (timeout !== null && Date.now() - startTime >= timeout) {
                console.warn("Read operation exceeded the specified timeout");
                break;
            }
        }

        return new Uint8Array(bytes);
    }

    // Flushes the current input buffer
    flushBuffer() {
        this.buffer = new Uint8Array(0); // Clear the buffer
    }
}

class Serprog {
    constructor() {
        this.debug = false
        this.name = "Serprog-based programmer"
        this.description = "Serprog is a serial-based flash dumper protocol."
        this.read_size = 256
        this.ready = false
    }

    async setupDevice(device) {
        console.log(device)
        this.device = device
        await this.device.open({baudRate: 115200})
        console.log(device)
        console.log("READABLE")
        console.log(this.device.readable)
        this.reader = new BufferedReader(this.device.readable.getReader())
        this.writer = this.device.writable.getWriter()
        
        // Request programmer name
        await this.writer.write(new Uint8Array([0x03]))
        const data = await this.reader.read(17, 1000)
        const decoder = new TextDecoder("ascii");
        this.name = decoder.decode(data) + " (Serprog)"
        console.log("Programmer found", data, "Name", this.name)
        this.ready = true
    }

    disconnect() {
        this.ready = false;
        if (this.device) {
            this.device.close();
            console.log("Device closed");
        }
    }

    async spiTransfer(write, read_length) {
        let cb = new Uint8Array([0x14])
        let wb = createUint24(write.length);
        let rb = createUint24(read_length);
        let b = concatTypedArrays(concatTypedArrays(concatTypedArrays(cb, wb), rb), write)
        if (this.debug) console.log("Write", write.length, read_length, b)
        await this.writer.write(b)
        const read =  await this.reader.read(read_length + 1, 4000)
        if (this.debug) console.log("Read", read)
        if (read[0] != 0x6) {
            throw("Invalid response from adapter")
        }

        // Other adapters return the data that was read while writing. Supporting this here as a hack.
        const write_buf = new Uint8Array(write.length);
        const full_data = concatTypedArrays(write_buf, read.slice(1))
        if (this.debug) console.log("Returning", full_data)
        return full_data;
    }
}

export {Serprog}